import { setLenisInstance } from '../blocks/favorites.js';

let lenisScrollTriggerConnected = false;
let rafId = null;

export const Animations = () => {
    const LenisLib = window.Lenis;

    if (!LenisLib) {
        console.error('Lenis is not loaded. Please load Lenis before initializing animations.');

        return null;
    }

    // Track the scroll direction from the raw input events. These fire before
    // Lenis's own handler (wheel is captured), so `prevent()` can read the
    // intended direction, which Lenis does not pass to the callback itself.
    let lastScrollDelta = 0;
    let touchStartY = 0;

    window.addEventListener(
        'wheel',
        (e) => {
            lastScrollDelta = e.deltaY;
        },
        { capture: true, passive: true },
    );
    window.addEventListener(
        'touchstart',
        (e) => {
            touchStartY = e.touches[0]?.clientY ?? 0;
        },
        { capture: true, passive: true },
    );
    window.addEventListener(
        'touchmove',
        (e) => {
            const currentY = e.touches[0]?.clientY ?? 0;
            // Finger moving up (currentY < startY) => scrolling content down.
            lastScrollDelta = touchStartY - currentY;
            touchStartY = currentY;
        },
        { capture: true, passive: true },
    );

    const lenis = new LenisLib({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        autoRaf: false,
        syncTouch: true,
        prevent: (node) => {
            // Only prevent on elements that are ACTUALLY scrollable,
            // not just any element with an overflow class
            const preventSelectors = [
                '.offcanvas',
                '.favorites-panel',
                '.wp-block-ssm-accordion__panel',
                '.filter-dropdown',
                '.broker-modal',
                '.wp-block-ssm-interactive-map__content',
                '.broker-modal__inner',
                '.wp-block-ssm-interactive-map__dropdown-list',
            ];

            const match = node.closest(preventSelectors.join(', '));
            if (!match) return false;

            // Not actually scrollable -> let Lenis scroll the page.
            if (match.scrollHeight <= match.clientHeight) return false;

            // Sub-pixel tolerance: scrollTop is often fractional (trackpads,
            // high-DPI, browser zoom) and the browser caps max scrollTop a couple
            // of pixels short of scrollHeight - clientHeight. Without slack the
            // bottom check can stay false at the real bottom, so `prevent` keeps
            // native scroll locked and the gesture never chains to the page.
            const BOUNDARY_TOLERANCE = 2;

            const atTop = match.scrollTop <= BOUNDARY_TOLERANCE;
            const atBottom =
                match.scrollTop + match.clientHeight >= match.scrollHeight - BOUNDARY_TOLERANCE;

            // At a boundary and pushing further in that direction: stop
            // preventing so the leftover scroll chains through to the page.
            if (lastScrollDelta > 0 && atBottom) return false;
            if (lastScrollDelta < 0 && atTop) return false;

            // Otherwise the inner element still has room -> keep native scroll.
            return true;
        },
    });

    setLenisInstance(lenis);
    window.lenisInstance = lenis;

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;

    if (gsap && ScrollTrigger) {
        connectLenisToScrollTrigger(lenis, gsap, ScrollTrigger);
    } else {
        window.addEventListener('gsap:loaded', () => {
            const gsap = window.gsap;
            const ScrollTrigger = window.ScrollTrigger;

            if (gsap && ScrollTrigger) {
                connectLenisToScrollTrigger(lenis, gsap, ScrollTrigger);
            } else {
                console.error('gsap:loaded event fired but GSAP/ScrollTrigger not found on window');
            }
        });

        startRAFLoop(lenis);
    }

    return lenis;
};

function startRAFLoop(lenis) {
    function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
}

function stopRAFLoop() {
    if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
    }
}

function connectLenisToScrollTrigger(lenis, gsap, ScrollTrigger) {
    if (lenisScrollTriggerConnected) return;

    stopRAFLoop();

    ScrollTrigger.config({
        limitCallbacks: true,
        ignoreMobileResize: true,
        autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load',
    });

    // Use the official pattern — no throttling wrapper
    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    lenisScrollTriggerConnected = true;
}
